import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, type ReactNode } from 'react';

import { useProfileNavSection } from '@/core/store/profileNavigation/profileNavigationSelectors';
import { mergeSx } from '@/utils/mergeSx';

import { sectionBlockStyles } from './SectionBlock.styles';

type Props = {
	id?: string;
	title: string;
	children: ReactNode;
	hideHeader?: boolean;
	controls?: ReactNode;
	customSx?: {
		root?: SxProps<Theme>;
		title?: SxProps<Theme>;
		section?: SxProps<Theme>;
	};
};

export const SectionBlock = ({
	id,
	title,
	children,
	hideHeader = false,
	controls,
	customSx
}: Props) => {
	const { addSection, removeSection } = useProfileNavSection();

	useEffect(() => {
		addSection({
			id: id ?? title,
			title: title
		});

		return () => {
			removeSection(id ?? title);
		};
	}, [id, title, addSection, removeSection]);

	return (
		<Box sx={mergeSx({ my: 0, px: 2 }, customSx?.root)} id={id ?? title}>
			{!hideHeader && (
				<Box sx={sectionBlockStyles.header}>
					<Typography
						variant='subtitle1'
						color='textFadedPrimary'
						sx={mergeSx(sectionBlockStyles.title, customSx?.title)}
					>
						{title}
					</Typography>
					{controls}
				</Box>
			)}
			<Box sx={mergeSx(sectionBlockStyles.section, customSx?.section)}>
				{children}
			</Box>
		</Box>
	);
};
